import { getMoldTypeAsString, MoldType, SIDE } from './consts-and-types';

const getStoredValue = (side, moldType, key, defaultValue) => {
  const type = typeof moldType === 'string' ? moldType : getMoldTypeAsString(moldType);

  try {
    const values = JSON.parse(readFromStorage('values'));
    return values?.[side]?.[type]?.[key] ?? defaultValue;
  } catch (error) {
    console.log(`Error reading value ${side}/${type}${key} from storage: ${error.message}`);
    return defaultValue;
  }
};

const getStoredCrustCheckboxValue = () => {
  const leftType = getMoldTypeAsString(getStoredMoldType(SIDE.Left, null));
  const rightType = getMoldTypeAsString(getStoredMoldType(SIDE.Right, null));

  if (leftType === null || rightType === null) {
    return false;
  }

  const leftCrustWidth = parseInt(getStoredValue(SIDE.Left, leftType, 'crust', 0), 10);
  const rightCrustWidth = parseInt(getStoredValue(SIDE.Right, rightType, 'crust', 0), 10);

  return leftCrustWidth + rightCrustWidth > 0;
};

const getStoredMoldCount = (side, defaultValue) => {
  const key = `${side}-count`;
  try {
    const count = parseInt(localStorage.getItem(key), 10);
    return isNaN(count) ? defaultValue : count;
  } catch (error) {
    console.log(`Error reading mold count ${key} from storage: ${error.message}`);
    return defaultValue;
  }
};

const getStoredMoldType = (side, defaultValue) => {
  const key = `${side}-type`;
  try {
    const type = localStorage.getItem(key);

    switch (type) {
      case 'round':
        return MoldType.Round;
      case 'rectangle':
        return MoldType.Rectangle;
      case 'silicone':
        return MoldType.Silicone;
      default:
        console.log(`Unknown mold type "${type}" (using default value "${defaultValue}")`);
        return defaultValue;
    }
  } catch (error) {
    console.log(`Error reading mold type ${key} from storage: ${error.message}`);
    return defaultValue;
  }
};

// eslint-disable-next-line no-unused-vars
const getStoredMoldTypeAsString = (side, defaultValue) => {
  const key = `${side}-type`;
  try {
    const type = localStorage.getItem(key);
    if (type === 'round' || type === 'rectangle' || type === 'silicone') {
      return type;
    }

    console.log(`Unknown mold type "${type}" (using default value "${defaultValue}")`);
    return defaultValue;
  } catch (error) {
    console.log(`Error reading mold type ${key} from storage: ${error.message}`);
    return defaultValue;
  }
};

// Store a specific value (e.g. left container, round mold, height, 2 cm)
const storeValue = (side, type, valueList) => {
  try {
    // Read stored values first
    const valuesStr = readFromStorage('values');
    const values = valuesStr ? JSON.parse(valuesStr) : {};

    // Update values
    if (!values[side]) {
      values[side] = {};
    }
    if (!values[side][type]) {
      values[side][type] = {};
    }

    valueList.forEach(([key, value]) => {
      values[side][type][key] = value;
    });

    saveToStorage('values', values);
  } catch (error) {
    console.log(`Error reading values from storage: ${error.message}`);
  }
};

// Save to local storage
const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
  } catch (error) {
    console.log(`Error storing value for key "${key}": ${error.message}`);
  }
};

// Read all values as an object from local storage
const readFromStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const parseRoundSettings = (side, settings) => {
  if (settings.length < 2) {
    return;
  }

  const [radius, height, crust] = settings;
  if (radius === '' || height === '') {
    return;
  }

  storeValue(side, 'round', [
    ['radius', parseFloat(radius)],
    ['height', parseFloat(height)],
    ['crust', typeof crust !== 'undefined' ? parseInt(crust, 10) : 0],
  ]);
};

const parseRectangleSettings = (side, settings) => {
  if (settings.length < 3) {
    return;
  }

  const [width, length, height, crust] = settings;
  if (width === '' || length === '' || height === '') {
    return;
  }

  storeValue(side, 'rectangle', [
    ['width', parseFloat(width)],
    ['length', parseFloat(length)],
    ['height', parseFloat(height)],
    ['crust', typeof crust !== 'undefined' ? parseInt(crust, 10) : 0],
  ]);
};

const parseSiliconeSettings = (side, settings) => {
  if (settings.length === 0) {
    return;
  }

  const [volume] = settings;

  if (volume === '') {
    return;
  }

  storeValue(side, 'silicone', [['volume', parseFloat(volume)]]);
};

const parseUrlSettings = (side, settingsStr) => {
  if (!/^[\d.-]+$/u.test(settingsStr)) {
    // Not valid settings
    return;
  }

  const [count, type, ...settings] = settingsStr.split('-');

  if (count === '' || type === '' || settings.length === 0) {
    return;
  }

  saveToStorage(`${side}-count`, count);

  if (type === '0') {
    // Round mold
    parseRoundSettings(side, settings);
    saveToStorage(`${side}-type`, 'round');
    return;
  }

  if (type === '1') {
    // Rectangle mold
    parseRectangleSettings(side, settings);
    saveToStorage(`${side}-type`, 'rectangle');
    return;
  }

  if (type === '2') {
    // Silicone mold
    parseSiliconeSettings(side, settings);
    saveToStorage(`${side}-type`, 'silicone');
  }
};

const initializeFromUrl = () => {
  const {
    location: { search },
  } = window;

  if (!search || !search.startsWith('?')) {
    return;
  }

  // Get rid of leading "?"
  const [left, right] = search.substring(1).split('&');

  parseUrlSettings(SIDE.Left, left);
  parseUrlSettings(SIDE.Right, right);
};

export { getStoredCrustCheckboxValue, initializeFromUrl, getStoredMoldCount, getStoredMoldType, getStoredValue, saveToStorage, storeValue };
